import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ArrowRightLeft, FileText, Database, RefreshCw } from 'lucide-react';
import { getSystemStatus, SystemStatusResponse } from '@/services/status';

export function HomePage() {
  const [status, setStatus] = useState<SystemStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSystemStatus();
      setStatus(data);
    } catch (err) {
      setError('Failed to fetch system status');
      console.error('Error fetching status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const getStatusDisplay = (connected: boolean, errorMsg?: string | null) => {
    if (connected) {
      return <span className="text-2xl font-semibold text-green-600">Connected</span>;
    }
    return (
      <span className="text-2xl font-semibold text-red-600" title={errorMsg || 'Disconnected'}>
        Disconnected
      </span>
    );
  };

  const tools = [
    {
      title: 'Reconciliation Batches',
      description: 'View and manage Salesforce opportunity batches for journal posting',
      icon: FileText,
      href: '/batches',
      color: 'bg-blue-500',
    },
    {
      title: 'WPG FX Converter',
      description: 'Convert Benevity disbursement CSV files into AUD with the derived FX rate',
      icon: ArrowRightLeft,
      href: '/wpg-fx-converter',
      color: 'bg-cyan-500',
    },
    {
      title: 'Sage Intacct',
      description: 'Manage code caches and view Sage Intacct integration status',
      icon: Database,
      href: '/sage',
      color: 'bg-green-500',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100">
          Welcome to FinanceHub
        </h1>
        <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
          A centralized hub for finance special tasks
        </p>
      </div>

      {/* Tools Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tools.map((tool) => {
          const Icon = tool.icon;
          return (
            <Link key={tool.title} to={tool.href} className="h-full">
              <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer">
                <div className="flex flex-col items-center text-center h-full">
                  <div className={`${tool.color} p-4 rounded-full`}>
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-4">
                    {tool.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mt-4 flex-grow">
                    {tool.description}
                  </p>
                  <Button variant="outline" className="w-full mt-4">
                    Open
                  </Button>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Quick Stats */}
      <Card title="System Status" className="mt-8">
        {loading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="text-center py-4">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <Button variant="outline" className="mt-2" onClick={fetchStatus}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">Salesforce</p>
                <p className="mt-1">
                  {getStatusDisplay(status?.services.salesforce.connected ?? false, status?.services.salesforce.error)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">Sage Intacct</p>
                <p className="mt-1">
                  {getStatusDisplay(status?.services.sage.connected ?? false)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">Azure Key Vault</p>
                <p className="mt-1">
                  {getStatusDisplay(status?.services.vault.connected ?? false)}
                </p>
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <Button variant="ghost" size="sm" onClick={fetchStatus}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
